import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { StyledAmountContainer } from "../components/containers/StyledAmountContainer";
import { StyledContainer } from "../components/containers/StyledContainer";
import AddAndEditInvoiceLineItem from "../components/invoice/AddAndEditInvoiceLineItem";
import DisplayInvoiceLineItem from "../components/invoice/DisplayInvoiceLineItem";
import InvoiceLineItem from "../components/invoice/InvoiceLineItem";
import Tooltip from "../components/Tooltip";
import Typography from "../components/Typography";
import UserType from "../constants/UserType";
import { getTaxDetails } from "../firebase/finance";
import { fetchUserSettings } from "../firebase/user";
import {
  calculateGrossTotal,
  calculateServiceCharge,
  calculateTax,
} from "../helper/finance";
import getInvoiceItemAmount from "../helper/getInvoiceItemAmount";
import { GigProposal, Invoice, InvoiceLineItems } from "../interfaces/Gig";
import { Settings } from "../interfaces/User";
import { theme } from "../mui-customs/Theme";
import { RootState } from "../redux/store";
import { formatAmountForDisplay } from "../utils/stripe-helpers";

type Props = {
  edit?: boolean;
};

const useInvoice = (gigProposalData?: GigProposal) => {
  const {
    uid,
    address: userLocation,
    userType,
  } = useSelector((state: RootState) => state.user.user);

  const [settings, setSettings] = useState<Settings>({
    tax_amount: 0,
    deposit_percentage: 0,
    service_charge: 0,
  });
  useEffect(() => {
    const getSettings = async () => {
      if (uid) {
        const settings = await fetchUserSettings(uid);
        if (settings) {
          setSettings(settings);
        }
      }
    };
    getSettings();
  }, [uid]);

  const [invoice, setInvoiceData] = useState<Invoice>(
    gigProposalData?.invoice || {
      lineItems: [],
      total: 0,
    }
  );

  useEffect(() => {
    gigProposalData?.invoice && setInvoiceData(gigProposalData?.invoice);
  }, [gigProposalData]);

  useEffect(() => {
    const getTaxes = async () => {
      if (uid && invoice.total > 0 && gigProposalData?.id) {
        const taxDetails = await getTaxDetails(
          gigProposalData?.id,
          invoice.total
        );

        setSettings((prev) => {
          return {
            ...prev,
            tax_amount: prev.tax_amount + taxDetails.data?.taxData,
          };
        });
      }
    };
    getTaxes();
  }, [invoice]);

  const handleInvoiceSubmit = (data: {
    title: string;
    rate: number;
    quantity: number;
  }) => {
    setInvoiceData((prevData) => {
      const newData = { ...prevData };
      newData.lineItems = newData.lineItems.concat({ ...data });
      newData.total += getInvoiceItemAmount(data);
      newData.total = newData.total;
      return newData;
    });
  };

  const handleEditInvoiceData = (
    invoiceData: InvoiceLineItems,
    index: number
  ) => {
    setInvoiceData((prevData) => {
      const newData: Invoice = { ...prevData };
      // Temp fix
      newData.total -= getInvoiceItemAmount(newData.lineItems[index]);
      const newLineItems = [...newData.lineItems];
      newLineItems[index] = { ...invoiceData };
      newData.lineItems = newLineItems;
      newData.total += getInvoiceItemAmount(invoiceData);
      newData.total = newData.total;
      return newData;
    });
  };

  const handleDeleteInvoiceItem = (index: number) => {
    setInvoiceData((prevInvoiceData) => {
      const copyInvoiceData: Invoice = { ...prevInvoiceData };
      copyInvoiceData.total -= getInvoiceItemAmount(
        copyInvoiceData.lineItems[index]
      );
      copyInvoiceData.lineItems.splice(index, 1);
      return copyInvoiceData;
    });
  };

  const displayTotal =
    invoice.lineItems.length > 0 &&
    settings &&
    userType &&
    formatAmountForDisplay(
      calculateGrossTotal(
        invoice.total,
        settings.tax_amount || 0,
        settings.service_charge,
        userType
      ),
      "usd"
    );
  let InvoiceComponent: FC<Props> = ({ edit = false }: Props) => (
    <StyledContainer>
      <Typography
        variant="body2"
        sx={{ color: `${theme.grey2[500]} !important` }}
      >
        Invoice
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 3,
        }}
      >
        {invoice.lineItems.map((lineItem, index) => {
          return (
            <>
              {edit ? (
                <InvoiceLineItem
                  key={index}
                  handleInvoiceDelete={() => {
                    handleDeleteInvoiceItem(index);
                  }}
                  handleInvoiceEdit={(data: InvoiceLineItems) =>
                    handleEditInvoiceData(data, index)
                  }
                  invoiceData={lineItem}
                />
              ) : (
                <DisplayInvoiceLineItem
                  view={true}
                  key={index}
                  invoiceData={lineItem}
                />
              )}
            </>
          );
        })}
        {edit ? (
          <AddAndEditInvoiceLineItem
            invoiceSubmitHandler={handleInvoiceSubmit}
          />
        ) : null}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 2,
          }}
        >
          <StyledAmountContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <Typography variant="small5">Subtotal</Typography>
              <Tooltip title={`This is the sum of all the invoice line items.`}>
                <InfoOutlinedIcon
                  sx={{ fontSize: "14px", color: theme.grey2[400] }}
                />
              </Tooltip>
            </Box>
            <Typography variant="title2">
              {formatAmountForDisplay(invoice.total, "usd")}
            </Typography>
          </StyledAmountContainer>
          {settings?.tax_amount != undefined ? (
            <StyledAmountContainer>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                <Typography variant="small5">Tax & Fees</Typography>
                <Tooltip
                  title={`These are the applicable taxes & fees required by your state, ${
                    userLocation?.state
                  }. In ${userLocation?.state} it is $ ${parseFloat(
                    `${settings.tax_amount / 100}`
                  ).toFixed(2)} .`}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: "14px", color: theme.grey2[400] }}
                  />
                </Tooltip>
              </Box>
              <Typography variant="title2">
                {userType === UserType.Chef && "-"}
                {invoice.lineItems.length > 0 &&
                  settings &&
                  formatAmountForDisplay(
                    calculateTax(invoice.total, settings.tax_amount || 0) || 0,
                    "usd"
                  )}
              </Typography>
            </StyledAmountContainer>
          ) : null}
          <StyledAmountContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <Typography variant="small5">Service Charge</Typography>
              <Tooltip title="This is the amount More Pleaze takes to run the marketplace.">
                <InfoOutlinedIcon
                  sx={{ fontSize: "14px", color: theme.grey2[400] }}
                />
              </Tooltip>
            </Box>
            <Typography variant="title2">
              {userType === UserType.Chef && "-"}
              {invoice.lineItems.length > 0 &&
                settings &&
                formatAmountForDisplay(
                  calculateServiceCharge(
                    invoice.total,
                    settings.service_charge
                  ),
                  "usd"
                )}
            </Typography>
          </StyledAmountContainer>
          <StyledAmountContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <Typography variant="small5">Your Total Payout</Typography>
              <Tooltip title="The sum of Subtotal, Tax & Fees, and Service Charge.">
                <InfoOutlinedIcon
                  sx={{ fontSize: "14px", color: theme.grey2[400] }}
                />
              </Tooltip>
            </Box>
            <Typography variant="title2">{displayTotal || "-"}</Typography>
          </StyledAmountContainer>
        </Box>
      </Box>
    </StyledContainer>
  );

  return { InvoiceComponent, invoice, settings };
};

export default useInvoice;
